import { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AssistantMessage as AssistantMessageType, GeneratedFileRef, WebCitation } from '@/types/assistant';
import GeneratedFileChip from './GeneratedFileChip';
import CitationChip from './CitationChip';
import DrlResult from './DrlResult';
import OutlinePreview from './OutlinePreview';
import ReviewResult from './ReviewResult';
import ModelBadge from './ModelBadge';
import { ModelCatalog } from '@/utils/modelIdentity';

interface Props {
  message: AssistantMessageType;
  /** Catálogo pra resolver `model_used` em nome+fornecedor. Sem ele, mostra o id cru. */
  modelCatalog?: ModelCatalog | null;
}

/** Dedupe de citações por URL preservando a ordem. */
function dedupeCitations(citations: WebCitation[]): WebCitation[] {
  const seen = new Set<string>();
  const out: WebCitation[] = [];
  for (const c of citations) {
    if (!c.url || seen.has(c.url)) continue;
    seen.add(c.url);
    out.push(c);
  }
  return out;
}

/** Dedupe de arquivos por file_id. */
function dedupeFiles(files: GeneratedFileRef[]): GeneratedFileRef[] {
  const seen = new Set<string>();
  const out: GeneratedFileRef[] = [];
  for (const f of files) {
    if (!f.file_id || seen.has(f.file_id)) continue;
    seen.add(f.file_id);
    out.push(f);
  }
  return out;
}

const AssistantMessage: FC<Props> = ({ message, modelCatalog }) => {
  const isUser = message.role === 'USER';
  // Mensagem do assistente com tabela markdown (ex.: playbook do H.Lex, 6 colunas) ganha
  // um balão BEM mais largo — o painel direito vem recolhido, então há espaço de sobra. A
  // rolagem horizontal cobre o resto. Texto comum fica num balão estreito (legibilidade).
  const hasTable = !isUser && /(^|\n)\s*\|.*\|/.test(message.content);
  const citations =
    !isUser && Array.isArray(message.metadata?.citations)
      ? dedupeCitations(message.metadata.citations)
      : [];
  const files =
    !isUser && Array.isArray(message.metadata?.generated_files)
      ? dedupeFiles(message.metadata.generated_files)
      : [];
  // S06: resultado estruturado do matching do DRL (H.Find) — renderiza abaixo da resposta.
  const drl =
    !isUser && message.metadata?.drl_result && Array.isArray(message.metadata.drl_result.itens)
      ? message.metadata.drl_result
      : null;
  // D04: roteiro do deck (H.Slide) — preview do outline abaixo da resposta.
  const outline =
    !isUser && message.metadata?.outline && Array.isArray(message.metadata.outline.slides)
      ? message.metadata.outline
      : null;
  // Q06: resultado estruturado da revisão de documento (H.Rev) — renderiza abaixo da resposta.
  const review =
    !isUser && message.metadata?.review && Array.isArray(message.metadata.review.findings)
      ? message.metadata.review
      : null;

  return (
    <div
      data-testid="assistant-message"
      data-role={message.role}
      className={`flex items-end gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && (
        <div className="shrink-0 w-8 h-8 rounded-full bg-accent/15 border border-accent/30 grid place-items-center text-accent">
          <span className="material-symbols-outlined text-lg leading-none">auto_awesome</span>
        </div>
      )}
      <div
        className={`min-w-0 max-w-[85%] rounded-2xl px-5 py-3 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.35)] ${
          hasTable ? 'md:max-w-[60rem]' : 'md:max-w-3xl'
        } ${
          isUser
            ? 'bg-accent/15 border border-accent/30 text-content-primary rounded-br-sm'
            : 'bg-surface-secondary border border-surface-border text-content-primary rounded-bl-sm'
        }`}
      >
        <div
          className="prose prose-lg prose-tema max-w-none break-words
            prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2
            prose-ul:my-2 prose-ol:my-2 prose-li:my-1
            prose-table:my-0 prose-table:text-sm prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1
            prose-th:text-left prose-td:align-top
            prose-strong:text-content-primary
            prose-code:text-accent prose-code:before:content-none prose-code:after:content-none
            prose-a:text-accent"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Tabela larga (ex.: playbook do H.Lex c/ 6 colunas) NÃO pode estourar o
              // balão — envolve num container com rolagem horizontal própria.
              table: (props) => (
                <div className="my-3 overflow-x-auto rounded-lg border border-surface-border">
                  <table className="min-w-full text-xs">{props.children}</table>
                </div>
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
        </div>

        {drl && <DrlResult result={drl} />}

        {outline && <OutlinePreview outline={outline} />}

        {review && <ReviewResult result={review} />}

        {files.length > 0 && (
          <div data-testid="generated-files" className="mt-3 pt-3 border-t border-surface-border">
            <p className="text-2xs uppercase tracking-wider text-content-tertiary mb-1.5 font-semibold">
              Arquivos
            </p>
            <div className="flex flex-wrap gap-1.5">
              {files.map((f) => (
                <GeneratedFileChip key={f.file_id} file={f} />
              ))}
            </div>
          </div>
        )}

        {citations.length > 0 && (
          <div data-testid="citations" className="mt-3 pt-3 border-t border-surface-border">
            <p className="text-2xs uppercase tracking-wider text-content-tertiary mb-1.5 font-semibold">
              Fontes
            </p>
            <div className="flex flex-wrap gap-1.5">
              {citations.map((c, i) => (
                <CitationChip key={`${c.url}:${i}`} citation={c} />
              ))}
            </div>
          </div>
        )}

        {/* Atribuição por RESPOSTA, não por sessão: o histórico é misto por natureza (um
            admin pode trocar o modelo do assistente a qualquer momento), então cada balão
            carrega o modelo que de fato gerou aquele texto. */}
        {!isUser && message.model_used && (
          <div className="mt-2 pt-2 border-t border-surface-border/60">
            <ModelBadge
              info={message.model_info}
              model={message.model_used}
              catalog={modelCatalog}
              variant="inline"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantMessage;
